import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { endpoints } from "../../../api/endpoints";
import { useNavigate } from "react-router-dom";

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerNumber, setCustomerNumber] = useState("");
  const [email, setEmail] = useState("");
  const [debouncedCustomerNumber, setDebouncedCustomerNumber] = useState("");
  const [debouncedEmail, setDebouncedEmail] = useState("");
  const navigate = useNavigate();

  // Debounce the search inputs
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomerNumber(customerNumber);
    }, 1000);

    return () => clearTimeout(timer);
  }, [customerNumber]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmail(email);
    }, 1000);

    return () => clearTimeout(timer);
  }, [email]);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const url =
          endpoints.MITCO_API_URL +
          endpoints.CSFA_BASE +
          endpoints.CSFA_SITE_ADMIN +
          "get_users/";

        const params = {};
        if (debouncedCustomerNumber.trim()) {
          params.customer_number = debouncedCustomerNumber.trim();
        }
        if (debouncedEmail.trim()) {
          params.email = debouncedEmail.trim();
        }

        const response = await axios.get(url, {
          params,
          withCredentials: true,
          headers: {
            Accept: "application/json",
          },
        });
        setUsers(response.data.users || []);
      } catch (err) {
        setError(err?.response?.data?.detail || "Failed to load users.");
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [debouncedCustomerNumber, debouncedEmail]);

  const clearFilters = () => {
    setCustomerNumber("");
    setEmail("");
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">User Manager</h2>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded shadow"
          onClick={() => navigate("/settings/create-user")}
        >
          Create User
        </button>
      </div>

      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Search by Customer Number
            </label>
            <input
              type="text"
              value={customerNumber}
              onChange={(e) => setCustomerNumber(e.target.value)}
              placeholder="Enter customer number"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Search by Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="flex justify-between items-center mt-3">
          <button
            onClick={clearFilters}
            className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors hover:cursor-pointer"
            disabled={!customerNumber && !email}
          >
            Clear Filters
          </button>

          <div className="text-xs text-gray-500">
            Search will update automatically 1 second after you stop typing.
            Leave both fields empty to show all users.
          </div>
        </div>
      </div>

      {loading && <div>Loading users...</div>}
      {error && <div className="text-red-500">{error}</div>}
      {!loading && !error && (
        <table className="min-w-full border text-sm bg-white/40 shadow-sm rounded-lg">
          <thead>
            <tr>
              <th className="border border-gray-500 px-2 py-1">Email</th>
              <th className="border border-gray-500 px-2 py-1">First Name</th>
              <th className="border border-gray-500 px-2 py-1">Last Name</th>
              <th className="border border-gray-500 px-2 py-1">
                Customer Number
              </th>
              <th className="border border-gray-500 px-2 py-1">Is Active</th>
              <th className="border border-gray-500 px-2 py-1">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td className="border border-gray-500 px-2 py-1">
                  {user.email}
                </td>
                <td className="border border-gray-500 px-2 py-1">
                  {user.first_name}
                </td>
                <td className="border border-gray-500 px-2 py-1">
                  {user.last_name}
                </td>
                <td className="border border-gray-500 px-2 py-1">
                  {user.customer_number || "-"}
                </td>
                <td className="border border-gray-500 px-2 py-1">
                  {user.is_active ? "Yes" : "No"}
                </td>
                <td className="border border-gray-500 px-2 py-1">
                  <button
                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-xs mr-2"
                    onClick={() => navigate(`/settings/edit-user/${user.id}`)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default UserManager;
